import { Brand } from '@growdash/growintel/generated';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

// Types
export type BrandSourceCategory = 'watchlist' | 'other';

export interface VirtualItem {
  type: 'brand' | 'header' | 'separator' | 'show-all' | 'loading';
  brand?: Brand;
  category?: BrandSourceCategory;
  title?: string;
  isSelected?: boolean;
}

// Constants
const PAGE_SIZE = 50;
const WATCHLIST_LIMIT = 5;
const SEARCH_MIN_LENGTH = 2;

// Query configuration
const QUERY_CONFIG = {
  staleTime: 5 * 60 * 1000,
  gcTime: 30 * 60 * 1000,
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  retry: 2,
};

// Utility functions
const createBrandQueryFn = (
  service: any,
  city?: string,
  area?: string,
  cuisine?: string,
  searchTerm?: string,
) => {
  return async ({ pageParam = 1 }: { pageParam?: number }) => {
    if (!service) throw new Error('Service not available');

    const response = await service.brandsAPI.getBrandByNameBrandsSearchGet(
      pageParam,
      PAGE_SIZE,
      searchTerm || undefined,
      city || undefined,
      area || undefined,
      cuisine || undefined,
    );

    return {
      items: response?.data?.items ?? [],
      nextPage:
        response?.data?.items?.length === PAGE_SIZE ? pageParam + 1 : undefined,
    };
  };
};

const getBrandCuisinesArray = (brand: Brand): string[] =>
  brand.cuisines?.split(',').map((c: string) => c.trim()) ?? [];

const createBrandMap = (brandArrays: Brand[][]): Map<string, Brand> => {
  const brandMap = new Map<string, Brand>();
  brandArrays.flat().forEach((brand) => {
    if (brand.id) {
      brandMap.set(brand.id, brand);
    }
  });
  return brandMap;
};

// Custom hooks
export function useBrandQueries(
  service: any,
  city?: string,
  area?: string,
  cuisine?: string,
  debouncedSearch?: string,
  renderedCategories?: BrandSourceCategory[],
) {
  const shouldQueryOther = renderedCategories?.includes('other');

  const initialQuery = useInfiniteQuery({
    queryKey: ['brands-initial', city, area, cuisine] as const,
    queryFn: createBrandQueryFn(service, city, area, cuisine || undefined),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled: !!service && !!shouldQueryOther,
    ...QUERY_CONFIG,
  });

  const searchQuery = useInfiniteQuery({
    queryKey: ['brands-search', debouncedSearch, city, area, cuisine] as const,
    queryFn: createBrandQueryFn(
      service,
      city,
      area,
      cuisine || undefined,
      debouncedSearch,
    ),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    enabled:
      !!service &&
      !!shouldQueryOther &&
      (debouncedSearch?.length ?? 0) >= SEARCH_MIN_LENGTH,
    ...QUERY_CONFIG,
  });

  const activeQuery =
    (debouncedSearch?.length ?? 0) >= SEARCH_MIN_LENGTH
      ? searchQuery
      : initialQuery;

  const brands = useMemo(() => {
    const data = activeQuery.data?.pages.flatMap((page) => page.items) ?? [];

    // Fallback to initial data if search yields no results
    if (
      data.length === 0 &&
      (debouncedSearch?.length ?? 0) >= SEARCH_MIN_LENGTH &&
      !searchQuery.isLoading &&
      initialQuery.data?.pages?.length
    ) {
      return initialQuery.data.pages.flatMap((page) => page.items) ?? [];
    }

    return data;
  }, [
    activeQuery.data,
    debouncedSearch,
    searchQuery.isLoading,
    initialQuery.data,
  ]);

  return { initialQuery, searchQuery, activeQuery, brands };
}

export function useCachedBrands(
  initialData?: Brand[][],
  searchData?: Brand[][],
): Brand[] {
  const queryClient = useQueryClient();

  const cachedBrands =
    queryClient.getQueryData<Brand[]>(['cached-brands']) ?? [];

  useEffect(() => {
    const brandMap = createBrandMap([
      cachedBrands,
      ...(initialData ?? []),
      ...(searchData ?? []),
    ]);

    const updatedBrands = Array.from(brandMap.values());

    if (updatedBrands.length > cachedBrands.length) {
      queryClient.setQueryData<Brand[]>(['cached-brands'], updatedBrands);
    }
  }, [initialData, searchData, cachedBrands, queryClient]);

  return cachedBrands;
}

export function useVirtualItems(
  watchlist: Brand[] | undefined,
  brands: Brand[],
  searchInput: string,
  cuisine: string | null | undefined,
  renderedCategories: BrandSourceCategory[],
  value: string | undefined,
  activeQuery: any,
  showAllWatchlist: boolean,
) {
  return useMemo((): VirtualItem[] => {
    const items: VirtualItem[] = [];

    // Filter watchlist brands
    const filteredWatchlistBrands = renderedCategories.includes('watchlist')
      ? (watchlist?.filter((wb: Brand) => {
          const nameMatch =
            wb.brand_name?.toLowerCase().includes(searchInput.toLowerCase()) ??
            false;
          const cuisineMatch = cuisine
            ? getBrandCuisinesArray(wb).includes(cuisine)
            : true;
          return nameMatch && cuisineMatch;
        }) ?? [])
      : [];

    // Filter other brands (exclude watchlist brands)
    const watchlistIds = new Set(watchlist?.map((wb) => wb.id) ?? []);
    const otherBrands = renderedCategories.includes('other')
      ? brands.filter((b) => !watchlistIds.has(b.id))
      : [];

    const hideCategoryTitles = renderedCategories.length <= 1;

    // Add watchlist section
    if (filteredWatchlistBrands.length > 0) {
      if (!hideCategoryTitles) {
        items.push({ type: 'header', title: 'In your watchlist' });
      }

      const watchlistToShow = showAllWatchlist
        ? filteredWatchlistBrands
        : filteredWatchlistBrands.slice(0, WATCHLIST_LIMIT);

      watchlistToShow.forEach((brand: Brand) => {
        items.push({
          type: 'brand',
          brand,
          category: 'watchlist',
          isSelected: brand.id === value,
        });
      });

      if (
        !showAllWatchlist &&
        filteredWatchlistBrands.length > WATCHLIST_LIMIT
      ) {
        items.push({ type: 'show-all' });
      }
    }

    // Add separator and other brands section
    if (items.length > 0 && otherBrands.length > 0) {
      items.push({ type: 'separator' });
    }

    if (
      filteredWatchlistBrands.length > 0 &&
      otherBrands.length > 0 &&
      !hideCategoryTitles
    ) {
      items.push({ type: 'header', title: 'More Brands' });
    }

    // Add loading state for initial load
    if (activeQuery.isLoading && brands.length === 0) {
      items.push({ type: 'loading' });
    } else {
      // Add other brands
      otherBrands.forEach((brand: Brand) => {
        items.push({
          type: 'brand',
          brand,
          category: 'other',
          isSelected: brand.id === value,
        });
      });

      // Add loading indicator for next page
      if (activeQuery.hasNextPage) {
        items.push({ type: 'loading' });
      }
    }

    return items;
  }, [
    watchlist,
    brands,
    searchInput,
    cuisine,
    renderedCategories,
    value,
    activeQuery.isLoading,
    activeQuery.hasNextPage,
    showAllWatchlist,
  ]);
}
